import React, { useState } from "react";
import axios from "axios";
import {
    Paper,
    Group,
    Text,
    Button,
    Table,
    Badge,
    Modal,
    Stack,
    TextInput,
    Switch,
    ActionIcon,
    Tooltip,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { IconPlus, IconPencil, IconTrash } from "@tabler/icons-react";
import type {
    TransportRouteDto,
    CreateTransportRouteRequest,
} from "../types";

const apiUrl = process.env.REACT_APP_API_URL;
const RED = "#ff5e5e";

type Props = {
    eventId: number | null;
    routes: TransportRouteDto[];
    refreshRoutes: () => Promise<void>;
    onRouteSelected?: (routeId: number | null) => void;
    selectedRouteId?: number | null;
};

export default function TransportRouteRoutesTab({
    eventId,
    routes,
    refreshRoutes,
    onRouteSelected,
    selectedRouteId,
}: Props) {
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState<TransportRouteDto | null>(null);

    const [nameDK, setNameDK] = useState("");
    const [nameEN, setNameEN] = useState("");
    const [directionCode, setDirectionCode] = useState("");
    const [isActive, setIsActive] = useState(true);
    const [saving, setSaving] = useState(false);

    const openCreate = () => {
        setEditing(null);
        setNameDK("");
        setNameEN("");
        setDirectionCode("");
        setIsActive(true);
        setModalOpen(true);
    };

    const openEdit = (route: TransportRouteDto) => {
        setEditing(route);
        setNameDK(route.nameDK ?? "");
        setNameEN(route.nameEN ?? "");
        setDirectionCode(route.directionCode ?? "");
        setIsActive(route.isActive);
        setModalOpen(true);
    };

    const save = async () => {
        if (!eventId) return;

        if (!nameDK.trim()) {
            showNotification({
                title: "Mangler",
                message: "Navn (DK) er påkrævet",
                color: "red",
            });
            return;
        }

        try {
            setSaving(true);

            if (!editing) {
                const payload: CreateTransportRouteRequest = {
                    eventID: eventId,
                    nameDK: nameDK.trim(),
                    nameEN: nameEN.trim() || null,
                    directionCode: directionCode.trim() || null,
                };

                await axios.post(`${apiUrl}/api/transport-route/admin/routes`, payload);

                showNotification({
                    title: "Gemt",
                    message: "Rute oprettet",
                });
            } else {
                await axios.put(
                    `${apiUrl}/api/transport-route/admin/routes/${editing.transportRouteID}`,
                    {
                        nameDK: nameDK.trim(),
                        nameEN: nameEN.trim() || null,
                        directionCode: directionCode.trim() || null,
                        isActive,
                    }
                );

                showNotification({
                    title: "Gemt",
                    message: "Rute opdateret",
                });
            }

            setModalOpen(false);
            await refreshRoutes();
        } catch (err) {
            console.error(err);
            showNotification({
                title: "Fejl",
                message: "Kunne ikke gemme rute",
                color: "red",
            });
        } finally {
            setSaving(false);
        }
    };

    const disableRoute = async (id: number) => {
        try {
            await axios.delete(`${apiUrl}/api/transport-route/admin/routes/${id}`);
            showNotification({
                title: "OK",
                message: "Rute deaktiveret",
            });
            await refreshRoutes();
        } catch (err) {
            console.error(err);
            showNotification({
                title: "Fejl",
                message: "Kunne ikke deaktivere rute",
                color: "red",
            });
        }
    };

    return (
        <>
            <Group justify="space-between" mb="sm">
                <Text fw={700}>Ruter</Text>

                <Button
                    leftSection={<IconPlus size={16} />}
                    onClick={openCreate}
                    disabled={!eventId}
                    style={{ backgroundColor: RED }}
                >
                    Opret rute
                </Button>
            </Group>

            <Paper withBorder p="md" radius="md">
                <Table.ScrollContainer minWidth={900}>
                    <Table striped highlightOnHover>
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th>Valgt</Table.Th>
                                <Table.Th>Navn (DK)</Table.Th>
                                <Table.Th>Navn (EN)</Table.Th>
                                <Table.Th>DirectionCode</Table.Th>
                                <Table.Th>Status</Table.Th>
                                <Table.Th></Table.Th>
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {routes.map((route) => (
                                <Table.Tr
                                    key={route.transportRouteID}
                                    style={{
                                        cursor: "pointer",
                                        backgroundColor:
                                            selectedRouteId === route.transportRouteID
                                                ? "rgba(255, 94, 94, 0.08)"
                                                : undefined,
                                    }}
                                    onClick={() =>
                                        onRouteSelected?.(route.transportRouteID)
                                    }
                                >
                                    <Table.Td>
                                        {selectedRouteId === route.transportRouteID ? "✓" : ""}
                                    </Table.Td>
                                    <Table.Td>{route.nameDK}</Table.Td>
                                    <Table.Td>{route.nameEN ?? "-"}</Table.Td>
                                    <Table.Td>{route.directionCode ?? "-"}</Table.Td>
                                    <Table.Td>
                                        <Badge
                                            color={route.isActive ? "green" : "gray"}
                                            variant="light"
                                        >
                                            {route.isActive ? "Active" : "Inactive"}
                                        </Badge>
                                    </Table.Td>
                                    <Table.Td>
                                        <Group justify="flex-end" gap="xs">
                                            <Tooltip label="Rediger">
                                                <ActionIcon
                                                    variant="light"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        openEdit(route);
                                                    }}
                                                >
                                                    <IconPencil size={16} />
                                                </ActionIcon>
                                            </Tooltip>

                                            <Tooltip label="Deaktiver">
                                                <ActionIcon
                                                    color="red"
                                                    variant="light"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        disableRoute(route.transportRouteID);
                                                    }}
                                                >
                                                    <IconTrash size={16} />
                                                </ActionIcon>
                                            </Tooltip>
                                        </Group>
                                    </Table.Td>
                                </Table.Tr>
                            ))}
                        </Table.Tbody>
                    </Table>
                </Table.ScrollContainer>
            </Paper>

            <Modal
                opened={modalOpen}
                onClose={() => setModalOpen(false)}
                title={editing ? "Rediger rute" : "Opret rute"}
                centered
            >
                <Stack>
                    <TextInput
                        label="Navn (DK)"
                        value={nameDK}
                        onChange={(e) => setNameDK(e.currentTarget.value)}
                    />

                    <TextInput
                        label="Navn (EN)"
                        value={nameEN}
                        onChange={(e) => setNameEN(e.currentTarget.value)}
                    />

                    <TextInput
                        label="DirectionCode"
                        placeholder="fx CITY_TO_ARENA"
                        value={directionCode}
                        onChange={(e) => setDirectionCode(e.currentTarget.value)}
                    />

                    {editing && (
                        <Switch
                            label="Aktiv"
                            checked={isActive}
                            onChange={(e) => setIsActive(e.currentTarget.checked)}
                        />
                    )}

                    <Group justify="flex-end">
                        <Button variant="default" onClick={() => setModalOpen(false)}>
                            Annullér
                        </Button>
                        <Button onClick={save} loading={saving}>
                            Gem
                        </Button>
                    </Group>
                </Stack>
            </Modal>
        </>
    );
}